import {
  ArrowUpTrayIcon,
  Button,
  Dialog,
  Flex,
  IconButton,
  Stepper,
  StepperStep,
  Tooltip,
} from 'components'
import { useCurrentUser } from 'components/providers'
import { isEmpty } from 'lodash-es'
import { useState } from 'react'
import { SessionWithLead } from 'types'
import { isProduction, useDisclosure, usePermission, useScrolling } from 'utils'
import { useEvent } from '../../../utils/eventUtils'
import SessionImportInit from './SessionImportInit'
import SessionImportMapping from './SessionImportMapping'
import { SessionImportProvider, useSessionImport } from './SessionImportProvider'
import SessionImportReview from './SessionImportReview'

interface Props {
  updateSessions: (body: SessionWithLead[]) => void
}

export default function SessionImportButton(props: Props) {
  const permission = usePermission()
  const user = useCurrentUser()
  const { event } = useEvent()
  const isManager = user?.id === event?.managerId

  if (isProduction() || (permission.event !== 'admin' && !isManager)) return null

  return (
    <SessionImportProvider {...props}>
      <SessionImportButtonContent />
    </SessionImportProvider>
  )
}

function SessionImportButtonContent() {
  const { overflowRef, contentRef, scrolling } = useScrolling()
  const { status } = useEvent()

  const {
    appTitle,
    appType,
    mainPropertyMap,
    momentId,
    onUpload,
    progress,
    resetState,
    selectedTab,
  } = useSessionImport()
  const { open, onOpen, onClose } = useDisclosure()
  const [stepIndex, setStepIndex] = useState(0)

  const enableStep2 = Boolean((appType === 'explorer' ? momentId != null : appTitle) && selectedTab)
  const enableStep3 = !isEmpty(mainPropertyMap)
  const steps: StepperStep[] = [
    { title: 'Upload', description: 'Upload File', canClick: true },
    { title: 'Mapping', description: 'Map Columns', canClick: enableStep2 },
    { title: 'Review', description: 'Finalize Process', canClick: enableStep3 },
  ]

  async function handleUpload() {
    await onUpload()
    handleDone()
  }

  function handleDone() {
    setStepIndex(0)
    resetState()
    onClose()
  }

  return (
    <>
      <Tooltip content="Import">
        <IconButton
          variant="solid"
          disabled={status !== 'success'}
          icon={<ArrowUpTrayIcon />}
          onClick={onOpen}
          borderRadius="sm"
          aria-label="Import"
        />
      </Tooltip>

      <Dialog
        size="xl"
        title="Import Sessions"
        bodyProps={{ gap: 6, display: 'flex', flexDirection: 'column', py: 6 }}
        open={open}
        onClose={handleDone}
      >
        <Stepper px={6} step={stepIndex} steps={steps} onChange={setStepIndex} />

        <Flex
          ref={overflowRef}
          flexDirection="column"
          overflow="auto"
          borderY={scrolling ? '1px' : 'none'}
          borderColor="gray.300"
        >
          {stepIndex === 0 ? (
            <SessionImportInit />
          ) : stepIndex === 1 ? (
            <SessionImportMapping ref={contentRef} />
          ) : (
            <SessionImportReview />
          )}
        </Flex>

        <Flex justifyContent="space-between" px={6}>
          <Button
            variant="outline"
            disabled={Boolean(progress)}
            onClick={() => {
              if (stepIndex > 0) setStepIndex(stepIndex - 1)
              else handleDone()
            }}
          >
            {stepIndex > 0 ? 'Back' : 'Cancel'}
          </Button>
          <Button
            palette="primary"
            disabled={
              stepIndex === 0 ? !enableStep2 : stepIndex === 1 ? !enableStep3 : Boolean(progress)
            }
            onClick={() => {
              if (stepIndex < steps.length - 1) setStepIndex(stepIndex + 1)
              else handleUpload()
            }}
          >
            {stepIndex === steps.length - 1 ? 'Finish' : 'Next'}
          </Button>
        </Flex>
      </Dialog>
    </>
  )
}
